import { ApiClientFactory } from '@/stores/apiClientFactory';

/**
 * Whether this organization has a subscription at all — the single question the router's subscription
 * gate asks. A new organization has none until it checks out, and the app is gated on paying, so
 * everything but the subscribe flow is closed to it.
 *
 * Lives here rather than in the router so the subscribe flow can correct the answer after checkout
 * without importing the router into the views it lazy-loads.
 */
type SubscriptionState = 'unknown' | 'has' | 'none';

/**
 * Answered once per page load rather than per navigation: it only changes by completing checkout, and
 * the pages that can change it say so through the functions below.
 */
let subscriptionState: SubscriptionState = 'unknown';

/**
 * Defaults to allowing access, and stays that way if the check fails. Getting this wrong in the strict
 * direction locks a paying customer out of their own admin over a failed request, which is far worse
 * than briefly letting an unsubscribed one in.
 */
async function readSubscriptionState(): Promise<SubscriptionState> {
    try {
        const client = ApiClientFactory.getInstance().getClient();
        const usage = await client.api.organizations.subscriptions.seats.get();
        // hasSubscription, not seats: a storefront-only plan legitimately includes no rep seats, and its
        // customer has certainly paid.
        return usage?.hasSubscription === false ? 'none' : 'has';
    } catch {
        return 'has';
    }
}

/** What the gate asks on every navigation, answered from the cache once it has an answer. */
export async function organizationHasSubscription(): Promise<boolean> {
    if (subscriptionState === 'unknown') {
        subscriptionState = await readSubscriptionState();
    }

    return subscriptionState === 'has';
}

/**
 * Drops the cached answer so the next navigation asks the API again. Called when entering the subscribe
 * flow, because anything learned before checkout is about to stop being true.
 */
export function forgetSubscriptionState(): void {
    subscriptionState = 'unknown';
}

/**
 * Records that checkout completed, whatever the API currently says. The success page knows something the
 * API may not have caught up with yet, and a webhook that lands late is not a reason to send a customer
 * who has just paid back to the pricing page.
 */
export function assumeSubscribed(): void {
    subscriptionState = 'has';
}

/**
 * Waits for the subscription to show up, for the gap between Stripe returning the browser and the
 * `customer.subscription.created` webhook recording it — the webhook can land after the redirect.
 * Resolves as soon as it appears, and leaves the cache holding whatever it last saw so the navigation
 * that follows doesn't repeat the call.
 *
 * A failed check counts as found, for the same fail-open reason as the read itself.
 */
export async function waitForSubscription(attempts = 6, delayMs = 1000): Promise<boolean> {
    for (let attempt = 0; attempt < attempts; attempt++) {
        if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }

        subscriptionState = await readSubscriptionState();
        if (subscriptionState === 'has') return true;
    }

    return false;
}
